import React, { useMemo, useState, useRef, Component } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HeadphonesIcon, ChevronRightIcon, StarIcon, TrendingUpIcon, UsersIcon, TagIcon, LibraryIcon, CalendarIcon } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { SearchWithAutocomplete, Suggestion } from '../components/UI/SearchWithAutocomplete';
import AudiobookCard from '../components/Books/AudiobookCard';
import { audiobooks as realAudiobooks } from '../AudioBookDetails';
import { FilterSidebar, FilterSidebarWrapper, NavItem } from '../components/UI/FilterSidebar';
// Build unified search suggestions
const buildSearchSuggestions = (): Suggestion[] => {
  const suggestions: Suggestion[] = [];
  const seenAuthors = new Set<string>();
  const seenNarrators = new Set<string>();
  realAudiobooks.forEach(book => {
    // Book titles
    suggestions.push({
      id: `book-${book.title}`,
      label: book.title,
      sublabel: `by ${book.author}`,
      type: 'Audiobook',
      image: book.coverImage
    });
    // Authors
    if (!seenAuthors.has(book.author)) {
      seenAuthors.add(book.author);
      suggestions.push({
        id: `author-${book.author}`,
        label: book.author,
        sublabel: 'Author',
        type: 'Author'
      });
    }
    // Narrators
    book.narrators.forEach(narrator => {
      if (!seenNarrators.has(narrator)) {
        seenNarrators.add(narrator);
        suggestions.push({
          id: `narrator-${narrator}`,
          label: narrator,
          sublabel: 'Narrator',
          type: 'Narrator'
        });
      }
    });
  });
  return suggestions;
};
const AudiobookNew = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const audioRefs = useRef<{
    [key: string]: HTMLAudioElement;
  }>({});
  const searchSuggestions = useMemo(() => buildSearchSuggestions(), []);
  const newReleases = [...realAudiobooks].reverse();
  const handleSearchSelect = (suggestion: Suggestion) => {
    if (suggestion.type === 'Audiobook') {
      const slug = suggestion.label.replace(/\s+/g, '-').toLowerCase();
      navigate(`/book/${slug}`);
    } else if (suggestion.type === 'Author') {
      const slug = suggestion.label.toLowerCase().replace(/\s+/g, '-');
      navigate(`/author/${slug}`);
    } else if (suggestion.type === 'Narrator') {
      const slug = suggestion.label.toLowerCase().replace(/\s+/g, '-');
      navigate(`/narrators/${slug}`);
    } else {
      navigate(`/search?q=${encodeURIComponent(suggestion.label)}`);
    }
    setSearchQuery('');
  };
  const handleSearch = (query: string) => {
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };
  const handlePlayPause = (bookId: string) => {
    if (currentlyPlaying === bookId) {
      if (audioRefs.current[bookId]) {
        audioRefs.current[bookId].pause();
      }
      setCurrentlyPlaying(null);
    } else {
      if (currentlyPlaying && audioRefs.current[currentlyPlaying]) {
        audioRefs.current[currentlyPlaying].pause();
      }
      if (audioRefs.current[bookId]) {
        audioRefs.current[bookId].play();
        setCurrentlyPlaying(bookId);
      }
    }
  };
  const handleAudioEnded = () => {
    setCurrentlyPlaying(null);
  };
  const audiobookNavItems: NavItem[] = [{
    label: 'Audiobooks',
    href: '/audiobooks',
    icon: HeadphonesIcon
  }, {
    label: 'Narrators',
    href: '/narrators',
    icon: UsersIcon
  }, {
    label: 'Genres',
    href: '/audiobooks/genres',
    icon: TagIcon
  }, {
    label: 'New Releases',
    href: '/audiobooks/new',
    icon: StarIcon,
    isActive: true
  }, {
    label: 'Bestsellers',
    href: '/audiobooks/bestsellers',
    icon: TrendingUpIcon
  }, {
    label: 'Series',
    href: '/series',
    icon: LibraryIcon
  }];
  return <div className={`min-h-screen ${dk.page}`} data-id="element-6496" data-mp="3k7swurrxuv7l">
      {/* Header with Search */}
      <div className={`py-10 ${dk.surface} border-b ${dk.border}`} data-id="element-6497" data-mp="3o8uoonjj04zj">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-6498" data-mp="3oap79v7brl0g">
          <div className={`flex items-center text-sm mb-4 ${dk.textMuted}`} data-id="element-6499" data-mp="3t5uvj0o79brp">
            <Link to="/" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-6500" data-mp="2265xf97banv">
              Home
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-6501" data-mp="23shnb0cyv3lb" />
            <Link to="/audiobooks" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-6502" data-mp="23u2fwkfq0cgj">
              Audiobooks
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-6503" data-mp="pxhxdsokdmyy" />
            <span className={dk.textPrimary} data-id="element-6504" data-mp="pyat7ylzis2b">New Releases</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6" data-id="element-6505" data-mp="l4gbv1tuqp6e">
            <div className="flex items-center gap-3" data-id="element-6506" data-mp="h4ecx0fi49ut">
              <CalendarIcon className={`h-8 w-8 ${rt.textPrimary}`} data-id="element-6507" data-mp="c5l6frymtj45" />
              <div data-id="element-6508" data-mp="18vc2vwqhhgcl">
                <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-6509" data-mp="18vu8vl2y78xp">
                  New Releases
                </h1>
                <p className={`mt-1 ${dk.textSecondary}`} data-id="element-6510" data-mp="14us7oyph0mt3">
                  Fresh audiobooks added this month
                </p>
              </div>
            </div>
            <div className="w-full md:max-w-md" data-id="element-6511" data-mp="uvt5nyz7npf2">
              <SearchWithAutocomplete value={searchQuery} onChange={setSearchQuery} suggestions={searchSuggestions} placeholder="Search new releases..." variant="rounded" onSelect={handleSearchSelect} onSearch={handleSearch} data-id="element-6512" data-mp="uv18ijpytjmo" />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-6513" data-mp="3m18y7lz9vjcr">
        <div className="flex gap-8" data-id="element-6514" data-mp="3817pxlgoh9n9">
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-6515" data-mp="384dcasxx9xfa">
            <FilterSidebar title="Browse" navigation={audiobookNavItems} sections={[]} onReset={() => {}} searchable={false} data-id="element-6516" data-mp="333fuwikql7eu" />
          </FilterSidebarWrapper>

          <main className="flex-1 min-w-0" data-id="element-6517" data-mp="2tdmkvm08t0cp">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6" data-id="element-6518" data-mp="2fd22us25j8k9">
              {newReleases.map((book, index) => <div key={`${book.title}-${index}`} className="flex justify-center" data-id="element-6519" data-mp="2kc58adm1ngsr" data-mpi={index}>
                  <div className="w-full max-w-[260px]" data-id="element-6520" data-mp="1vrtgmnlhffkg">
                    <AudiobookCard title={book.title} author={book.author} coverImage={book.coverImage} narrators={book.narrators} audioLength={book.audioLength} genre={book.genre} relationshipTypes={book.relationshipTypes} audioSampleUrl={book.audioSampleUrl || ''} isPlaying={currentlyPlaying === book.title} onPlayPause={handlePlayPause} spiceLevel={book.spiceLevel} tropes={book.tropes} seriesName={book.series?.name || undefined} seriesNumber={book.series?.number || undefined} seriesStatus={book.series?.seriesComplete ? 'completed' : book.series?.name ? 'ongoing' : undefined} isNew={true} data-id="element-6521" data-mp="1vt3f3dw8yprm" />
                    <audio ref={el => {
                  if (el) audioRefs.current[book.title] = el;
                }} src={book.audioSampleUrl} onEnded={handleAudioEnded} className="hidden" data-id="element-6522" data-mp="ika7g0y1icvn" />
                  </div>
                </div>)}
            </div>
          </main>
        </div>
      </div>
    </div>;
};
export default AudiobookNew;